import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FormGroup, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { co, en } from '@fullcalendar/core/internal-common';
import { Store } from '@ngxs/store';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { QuestionnaireConfigHttpService } from '@services/datasources/questionnaireConfig-http.service';
import { UtilsService } from '@services/common/utils.service';
import { QuestionnaireAction, QuestionnaireState } from '@state/questionnaire.state';
import { EnumEntryDto } from 'app/@types/dto/enum-entry-dto.model';
import { QuestionDto } from 'app/@types/dto/question-dto.model';
import { QuestionInstanceDto } from 'app/@types/dto/question-instance-dto.model';
import { forkJoin, map, Subject, takeUntil } from 'rxjs';
import { QuestionInstanceComponent } from './question-instance/question-instance.component';

export interface QuestionInstanceInputEntry {
    instanceId: string | undefined;
    instance: QuestionInstanceDto;
    editForm: FormGroup;
    value1Control: UntypedFormControl;
    value2Control: UntypedFormControl;
    value3Control: UntypedFormControl;
    unitControl: UntypedFormControl;
    value1ControlInitValue: string;
    value2ControlInitValue: string;
    value3ControlInitValue: string;
    unitControlInitValue: string;
    unitFromSelectedDropdown?: string;
    isDisabledLike: boolean;
    input1UpdateFailed?: boolean;
    input2UpdateFailed?: boolean;
    input3UpdateFailed?: boolean;
    hidden?: boolean;
    possibleUnits?: string[];
    isPrefilled?: boolean;
}

export interface inputUpdateMessage {
    inputEntry: QuestionInstanceInputEntry;
    questionField: number;
}

export type EnumEntryTranslatedDTO = EnumEntryDto & { translatedLabel?: string };

@Component({
    selector: 'app-question',
    templateUrl: './question.component.html',
    styleUrls: ['./question.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class QuestionComponent implements OnInit, OnDestroy {
    @Input() question: QuestionDto;
    @Input() domainPerCode: { [key: string]: EnumEntryDto[] };
    @Input() orgId: string;
    @Input() questionnaireId: string;
    @Input() locale: string;
    @Input() pageQuestions: QuestionDto[];
    @Input() parentQuestionInstance?: QuestionInstanceInputEntry;
    @ViewChild('subQuestionComponent') childComponent: QuestionComponent;
    @Input() currentEntityFte: number;

    public _fb: UntypedFormBuilder;

    private ngUnsubscribe = new Subject<void>();
    public isInit: boolean = false;
    public questionInstancesInputEntries: QuestionInstanceInputEntry[] = [];
    public hasNoNewEntry: boolean = false;
    public maxInstances: number = 0;
    public domain1: EnumEntryTranslatedDTO[] = [];
    public domain1Map: Map<string, EnumEntryTranslatedDTO> = new Map();
    public subQuestion?: QuestionDto;
    public nonHiddenEntries: QuestionInstanceInputEntry[];
    public activeIndex: number = -1;
    tooltipContent: string;

    constructor(
        private fb: UntypedFormBuilder,
        private store: Store,
        private translateService: CustomTranslateService,
        private cdr: ChangeDetectorRef,
        private questionnaireConfigService: QuestionnaireConfigHttpService,
        private ngZone: NgZone,
        private readonly utilsService: UtilsService,
    ) {
        this._fb = fb;
    }

    onLabelClick(id: string) {
        const input = document.getElementById(id);
        if (input) {
            const inputElement = this.findInputElement(input);
            inputElement?.focus();
        }
    }

    ngOnInit(): void {
        if (this.question.domainValue1) {
            this.maxInstances = Object.keys(this.domainPerCode[this.question.domainValue1]).length;
            this.domain1Map = this.domainPerCode[this.question.domainValue1].reduce((map, dto) => {
                map.set(dto.value, dto);
                return map;
            }, new Map<string, EnumEntryDto>());
        }

        this.store
            .selectOnce(QuestionnaireState.getQuestionInstances)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((map) => {
                let questionInstances = map!.get(this.question.questionCode) ?? [];
                if (this.question.option?.isSubQuestion) {
                    questionInstances = questionInstances.filter(
                        (qI) => qI.relatedQuestionInstanceId === this.parentQuestionInstance?.instanceId,
                    );
                }

                this.domain1 = Array.from(this.domain1Map.values()).map((elt) => {
                    return {
                        ...elt,
                        translatedLabel: this.translateService.instant(
                            `enum.${this.domain1Map.get(elt.value)!.domain}.${this.domain1Map.get(elt.value)!.value}`,
                        ),
                    };
                });

                if (!this.isInit) {
                    if (questionInstances && questionInstances?.length !== 0) {
                        let tempMap: Map<string, EnumEntryDto> = new Map();

                        if (this.question.value1Type === 'enum') tempMap = new Map([...this.domain1Map]);

                        for (let i = 0; i < questionInstances.length; i++) {
                            this.AddQuestionInstanceInputEntry(questionInstances[i], undefined);

                            if (this.question.value1Type === 'enum') {
                                tempMap.delete(questionInstances[i]!.value1!);
                            }
                        }

                        if (this.question.value1Type === 'enum')
                            this.domain1 = Array.from(tempMap.values()).map((elt) => {
                                return {
                                    ...elt,
                                    translatedLabel: this.translateService.instant(
                                        `enum.${this.domain1Map.get(elt.value)!.domain}.${
                                            this.domain1Map.get(elt.value)!.value
                                        }`,
                                    ),
                                };
                            });
                    }
                    this.preloadTooltipContent();
                    this.sortOptions();
                    this.handleEmptyEntriesSetup();
                    this.handleHiddenEntries();
                    this.hasNoNewEntry = false;
                    this.isInit = true;
                    this.cdr.detectChanges();
                }
            });

        this.translateService.onLangChange.subscribe((_) => {
            this.preloadTooltipContent();
            this.sortOptions();
        });

        if (this.question.option?.subQuestionCode) {
            this.subQuestion = this.pageQuestions.find(
                (question) => question.questionCode === this.question.option?.subQuestionCode,
            );
        }
    }

    private sortOptions(): void {
        this.domain1.forEach((elt) => {
            elt.translatedLabel = this.translateService.instant(
                `enum.${this.domain1Map.get(elt.value)!.domain}.${this.domain1Map.get(elt.value)!.value}`,
            );
        });

        this.domain1.sort((a, b) => {
            return a.translatedLabel!.toLowerCase().localeCompare(b.translatedLabel!.toLowerCase());
        });
    }

    private handleEmptyEntriesSetup(): void {
        let itCount: number =
            (this.question.isDynamic ? this.maxInstances : 1) -
            this.questionInstancesInputEntries.filter((i) => i.instanceId).length;
        for (let i = 0; i < itCount; i++) this.AddQuestionInstanceInputEntry();
    }

    public sortEntries(): void {
        this.questionInstancesInputEntries.sort((a, b) =>
            a.instanceId && b.instanceId ? 0 : a.instanceId && !b.instanceId ? -1 : 1,
        );
    }

    public handleHiddenEntries(): void {
        for (let i = 0; i < this.questionInstancesInputEntries.length; i++)
            if (!this.questionInstancesInputEntries[i].instanceId) {
                this.questionInstancesInputEntries[i].hidden = i > 0;
            }
        this.nonHiddenEntries = this.questionInstancesInputEntries.filter((entry) => !entry.hidden);
    }

    private getDefaultValues(questionInstance: QuestionInstanceDto | undefined): {
        value1?: string;
        value2?: string;
        value3?: string;
        unit?: string;
        shouldRefresh: number[];
    } {
        const obj: { value1?: string; value2?: string; value3?: string; unit?: string; shouldRefresh: number[] } = {
            value1: questionInstance?.value1,
            value2: questionInstance?.value2,
            value3: questionInstance?.value3,
            unit: questionInstance?.unit,
            shouldRefresh: [],
        };
        if (
            questionInstance?.value1 === undefined &&
            this.question.option?.questionnaireInitParameter?.value1 != undefined
        ) {
            obj.value1 = this.question.option?.questionnaireInitParameter?.value1;
            obj.shouldRefresh.push(1);
        }
        if (
            questionInstance?.value2 === undefined &&
            this.question.option?.questionnaireInitParameter?.value2 != undefined
        ) {
            obj.value2 = this.question.option?.questionnaireInitParameter?.value2;
            obj.shouldRefresh.push(2);
        }
        if (
            questionInstance?.value3 === undefined &&
            this.question.option?.questionnaireInitParameter?.value3 != undefined
        ) {
            obj.value3 = this.question.option?.questionnaireInitParameter?.value3;
            obj.shouldRefresh.push(3);
        }
        if (
            questionInstance?.unit === undefined &&
            [this.question.unitValue1, this.question.unitValue2, this.question.unitValue3].some(
                (unit) => unit != undefined,
            )
        ) {
            obj.unit = this.question.unitValue1 ?? this.question.unitValue2 ?? this.question.unitValue3;
            obj.shouldRefresh.push(4);
        }
        return obj;
    }

    private updateInputEntry(questionInstance: QuestionInstanceDto, input: QuestionInstanceInputEntry): void {
        const { value1, value2, value3, unit, shouldRefresh } = this.getDefaultValues(questionInstance);

        let value1Control = this._fb.control(value1 ?? null);
        let value2Control = this._fb.control(value2 ?? null);
        let value3Control = this._fb.control(value3 ?? null);
        let unitControl = this._fb.control(unit ?? null);

        let editForm = this._fb.group({
            value1Control: value1Control,
            value2Control: value2Control,
            value3Control: value3Control,
            unitControl: unitControl,
        });

        input.value1ControlInitValue = questionInstance!.value1 ?? '';
        input.value2ControlInitValue = questionInstance!.value2 ?? '';
        input.value3ControlInitValue = questionInstance!.value3 ?? '';
        input.unitControlInitValue = questionInstance!.unit ?? '';
        input.value1Control = value1Control;
        input.value2Control = value2Control;
        input.value3Control = value3Control;
        input.unitControl = unitControl;
        input.editForm = editForm;
        input.instance = questionInstance;
        input.instanceId = questionInstance.id;
        if (questionInstance && questionInstance.value1) {
            let enumEntry = this.domain1Map.get(questionInstance.value1);
            input.possibleUnits = enumEntry?.possibleUnits;
        }
        input.editForm.markAsPristine();
        this.cdr.detectChanges();
        if (shouldRefresh.length !== 0) {
            shouldRefresh.forEach((qF) => this.onInputEscape({ inputEntry: input, questionField: qF }));
        }
    }

    public hasNotEmptyInput(): boolean {
        return this.questionInstancesInputEntries.filter((i) => i.instanceId === undefined && !i.hidden).length === 0;
    }

    private resetInputEntry(input: QuestionInstanceInputEntry): void {
        const shouldReset: number[] = [];
        if (this.question.option?.questionnaireInitParameter?.value1 !== undefined) shouldReset.push(1);
        if (this.question.option?.questionnaireInitParameter?.value2 !== undefined) shouldReset.push(2);
        if (this.question.option?.questionnaireInitParameter?.value3 !== undefined) shouldReset.push(3);
        shouldReset.push(4);

        let value1Control = this._fb.control(this.question.option?.questionnaireInitParameter?.value1 ?? null);
        let value2Control = this._fb.control(this.question.option?.questionnaireInitParameter?.value2 ?? null);
        let value3Control = this._fb.control(this.question.option?.questionnaireInitParameter?.value3 ?? null);
        let unitControl = this._fb.control(
            this.question.unitValue1 ?? this.question.unitValue2 ?? this.question.unitValue3 ?? null,
        );

        let editForm = this._fb.group({
            value1Control: value1Control,
            value2Control: value2Control,
            value3Control: value3Control,
            unitControl: unitControl,
        });
        input.unitFromSelectedDropdown = undefined;
        input.value1ControlInitValue = '';
        input.value2ControlInitValue = '';
        input.value3ControlInitValue = '';
        input.unitControlInitValue = '';
        input.value1Control = value1Control;
        input.value2Control = value2Control;
        input.value3Control = value3Control;
        input.unitControl = unitControl;
        input.editForm = editForm;
        input.instance = {
            questionCode: this.question.questionCode,
            questionnaireId: this.questionnaireId,
            value1: this.question.option?.questionnaireInitParameter?.value1 ?? '',
            value2: this.question.option?.questionnaireInitParameter?.value2 ?? '',
            value3: this.question.option?.questionnaireInitParameter?.value3 ?? '',
            unit: this.question.unitValue1 ?? this.question.unitValue2 ?? this.question.unitValue3 ?? '',
        };
        input.possibleUnits = undefined;
        input.instanceId = undefined;
        input.hidden = true;
        input.editForm.markAsPristine();
        this.cdr.detectChanges();
        if (shouldReset.length !== 0)
            shouldReset.forEach((qF) => this.onInputEscape({ inputEntry: input, questionField: qF }));
    }

    private AddQuestionInstanceInputEntry(questionInstance?: QuestionInstanceDto, hasToRefresh?: boolean): void {
        const { value1, value2, value3, unit, shouldRefresh } = this.getDefaultValues(questionInstance);

        let value1Control = this._fb.control(value1 ?? null);
        let value2Control = this._fb.control(value2 ?? null);
        let value3Control = this._fb.control(value3 ?? null);
        let unitControl = this._fb.control(unit ?? null);

        let editForm = this._fb.group({
            value1Control: value1Control,
            value2Control: value2Control,
            value3Control: value3Control,
            unitControl: unitControl,
        });

        editForm.markAsPristine();
        let questionInstancesInputEntry: QuestionInstanceInputEntry = {
            value1ControlInitValue: questionInstance?.value1 ?? '',
            value2ControlInitValue: questionInstance?.value2 ?? '',
            value3ControlInitValue: questionInstance?.value3 ?? '',
            unitControlInitValue: questionInstance?.unit ?? '',
            value1Control: value1Control,
            value2Control: value2Control,
            value3Control: value3Control,
            unitControl: unitControl,
            editForm: editForm,
            instanceId: questionInstance?.id ?? undefined,
            instance: questionInstance ?? {
                questionCode: this.question.questionCode,
                questionnaireId: this.questionnaireId,
                value1: '',
                value2: '',
                value3: '',
                unit: '',
            },
            isDisabledLike: false,
            isPrefilled: questionInstance?.preFilled,
        };

        if (questionInstance && questionInstance.value1) {
            let enumEntry = this.domain1Map.get(questionInstance.value1);
            questionInstancesInputEntry.possibleUnits = enumEntry?.possibleUnits;
            if (questionInstance.unit && !enumEntry?.possibleUnits.includes(questionInstance.unit))
                questionInstancesInputEntry.possibleUnits?.push(questionInstance.unit);
        }
        this.questionInstancesInputEntries.push(questionInstancesInputEntry);
        if (hasToRefresh) {
            this.hasNoNewEntry = false;
        }
        if (shouldRefresh.length !== 0) {
            shouldRefresh.forEach((qF) =>
                this.onInputEscape({ inputEntry: questionInstancesInputEntry, questionField: qF }),
            );
        }
    }

    public insertEntry() {
        let shownInputCandidate = this.questionInstancesInputEntries.find((i) => !i.instanceId && i.hidden);
        if (shownInputCandidate) {
            shownInputCandidate.hidden = false;
            this.nonHiddenEntries = [...this.nonHiddenEntries, shownInputCandidate];
            this.ngZone.run(() => {
                this.activeIndex = this.nonHiddenEntries.length - 1;
            });
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    onEnumChange(inputEntry: QuestionInstanceInputEntry): void {
        if (inputEntry.value1Control.value) {
            let enumEntry = this.domain1Map.get(inputEntry.value1Control.value);
            inputEntry.possibleUnits = enumEntry?.possibleUnits;
            inputEntry.unitControl.setValue(enumEntry?.possibleUnits ? enumEntry.possibleUnits[0] : null);
            if (enumEntry?.defaultValue && (!inputEntry.value2Control.value || !inputEntry.instanceId)) {
                inputEntry.value2Control.setValue(enumEntry.defaultValue);
            }
        } else {
            inputEntry.unitFromSelectedDropdown = '';
        }
        this.cdr.detectChanges();
    }

    private updateDomain(): void {
        if (this.question.value1Type === 'enum') {
            this.questionInstancesInputEntries.forEach((ie) => {
                if (this.domain1Map.has(ie.value1Control.value)) {
                    let index = this.domain1.findIndex((e) => e.value === ie.value1Control.value);
                    if (index !== -1) {
                        this.domain1.splice(index, 1);
                        this.sortOptions();
                    }
                }
            });
            this.cdr.detectChanges();
        }
    }

    private restoreDomainEntry(input: QuestionInstanceInputEntry, oldValue?: boolean): void {
        if (
            oldValue
                ? this.domain1Map.get(input.value1ControlInitValue)
                : this.domain1Map.get(input.value1Control.value)
        ) {
            this.domain1.splice(
                0,
                0,
                this.domain1Map.get(oldValue ? input.value1ControlInitValue : input.value1Control.value)!,
            );
            this.sortOptions();
        }
        this.cdr.detectChanges();
    }

    onInputEscape(inputMessage: inputUpdateMessage) {
        let inputEntry = inputMessage.inputEntry;
        let questionField = inputMessage.questionField;
        let callCompletion: Subject<QuestionInstanceDto | undefined> = new Subject<QuestionInstanceDto | undefined>();
        let instance: QuestionInstanceDto;
        if (
            (inputEntry.instanceId &&
                ((inputEntry.value1Control.value?.toString() !== inputEntry.value1ControlInitValue &&
                    questionField === 1) ||
                    (inputEntry.value2Control.value?.toString() !== inputEntry.value2ControlInitValue &&
                        questionField === 2) ||
                    (inputEntry.value3Control.value?.toString() !== inputEntry.value3ControlInitValue &&
                        questionField === 3) ||
                    (inputEntry.unitControl.value?.toString() !== inputEntry.unitControlInitValue &&
                        questionField === 4))) ||
            (!inputEntry.instanceId &&
                ((this.question.isDynamic && inputEntry.value1Control.value !== null) || !this.question.isDynamic) &&
                ((inputEntry.value1Control.value !== null &&
                    inputEntry.value1Control.value.toString() !== inputEntry.value1ControlInitValue &&
                    questionField === 1) ||
                    (inputEntry.value2Control.value !== null &&
                        inputEntry.value2Control.value.toString() !== inputEntry.value2ControlInitValue &&
                        questionField === 2) ||
                    (inputEntry.value3Control.value !== null &&
                        inputEntry.value3Control.value.toString() !== inputEntry.value3ControlInitValue &&
                        questionField === 3)))
        ) {
            if (
                (inputEntry.instanceId && !this.question.value2Type && inputEntry.value1Control.value === null) ||
                (this.question.value2Type &&
                    inputEntry.value1Control.value === null &&
                    inputEntry.value2Control.value === null &&
                    inputEntry.value3Control.value === null)
            ) {
                this.deleteEntry(inputEntry);
                return;
            }
            if (!inputEntry.instanceId) {
                instance = {
                    questionCode: this.question.questionCode,
                    questionnaireId: this.questionnaireId,
                    value1: inputEntry.value1Control.value === '' ? undefined : inputEntry.value1Control.value,
                    value2: inputEntry.value2Control.value === '' ? undefined : inputEntry.value2Control.value,
                    value3: inputEntry.value3Control.value === '' ? undefined : inputEntry.value3Control.value,
                    unit:
                        inputEntry.possibleUnits && inputEntry.possibleUnits.length > 1
                            ? inputEntry.unitControl.value
                            : inputEntry.possibleUnits
                            ? inputEntry.possibleUnits[0]
                            : this.question.unitValue1 ?? this.question.unitValue2 ?? this.question.unitValue3,
                };
                inputEntry.instance = instance;
            } else {
                instance = inputEntry.instance;
                instance.value1 = inputEntry.value1Control.value === '' ? undefined : inputEntry.value1Control.value;
                instance.value2 = inputEntry.value2Control.value === '' ? undefined : inputEntry.value2Control.value;
                instance.value3 = inputEntry.value3Control.value === '' ? undefined : inputEntry.value3Control.value;
                instance.unit =
                    inputEntry.possibleUnits && inputEntry.possibleUnits.length > 1
                        ? inputEntry.unitControl.value
                        : inputEntry.possibleUnits
                        ? inputEntry.possibleUnits[0]
                        : this.question.unitValue1 ?? this.question.unitValue2 ?? this.question.unitValue3;
            }
            if (this.question.value1Type === 'enumRadio') {
                inputEntry.editForm.disable();
            }
            inputEntry.isDisabledLike = true;

            this.updateDomain();
            this.cdr.detectChanges();
            callCompletion.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
                next: (qqi) => {
                    if (qqi) {
                        if (inputEntry.instanceId) this.restoreDomainEntry(inputEntry, true);
                        this.updateInputEntry(qqi, inputEntry);
                    } else {
                        this.restoreDomainEntry(inputEntry);
                        inputEntry.value1Control.setValue(inputEntry.value1ControlInitValue);
                        inputEntry.value2Control.setValue(inputEntry.value2ControlInitValue);
                        inputEntry.value3Control.setValue(inputEntry.value3ControlInitValue);
                        this.handleCompletionMessage(inputEntry, questionField);
                    }

                    inputEntry.isDisabledLike = false;
                    this.hasNoNewEntry = true;
                    this.updateDomain();
                    this.cdr.detectChanges();
                    callCompletion.complete();

                    if (this.question.value1Type === 'enumRadio') {
                        inputEntry.editForm.enable();
                        this.cdr.detectChanges();
                    }
                },
            });

            if (this.parentQuestionInstance) {
                instance.relatedQuestionInstanceId = this.parentQuestionInstance.instanceId;
            }
            this.store.dispatch(
                new QuestionnaireAction.CreateUpdateAction(this.orgId, instance, questionField, callCompletion),
            );
        }
    }

    deleteEntry(inputEntry: QuestionInstanceInputEntry): void {
        let callCompletion: Subject<QuestionInstanceDto[] | undefined> = new Subject<
            QuestionInstanceDto[] | undefined
        >();
        inputEntry.isDisabledLike = true;
        this.cdr.detectChanges();
        callCompletion.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
            next: (qqi) => {
                if (qqi) {
                    if (this.question.isDynamic) this.restoreDomainEntry(inputEntry);
                    this.resetInputEntry(inputEntry);
                    this.sortOptions();
                    this.handleHiddenEntries();
                    if (this.subQuestion) {
                        const subQIToRemove = qqi.find((q) => q.questionCode === this.subQuestion?.questionCode);
                        if (subQIToRemove) this.removeChildComponent(subQIToRemove);
                    }
                    this.hasNoNewEntry = true;
                } else {
                    this.handleCompletionMessage(inputEntry, this.question.value2Type ? 2 : 1);
                }
                inputEntry.isDisabledLike = false;
                callCompletion.complete();
            },
        });
        this.store.dispatch(new QuestionnaireAction.DeleteAction(this.orgId, inputEntry.instance, callCompletion));
    }

    removeChildComponent(qI: QuestionInstanceDto): void {
        if (this.childComponent) {
            this.childComponent.removeChildEntry(qI);
        }
    }

    removeChildEntry(qI: QuestionInstanceDto): void {
        const inputEntry = this.questionInstancesInputEntries.find((i) => i.instanceId === qI.id);
        if (inputEntry) {
            inputEntry.value1Control.setValue(null);
            inputEntry.value2Control.setValue(null);
            inputEntry.value3Control.setValue(null);
        }
    }

    handleCompletionMessage(input: QuestionInstanceInputEntry, field: number) {
        if (field === 2) {
            input.input2UpdateFailed = true;
            setTimeout(() => {
                input.input2UpdateFailed = false;
                this.cdr.detectChanges();
            }, 1500);
        } else {
            input.input1UpdateFailed = true;
            setTimeout(() => {
                input.input1UpdateFailed = false;
                this.cdr.detectChanges();
            }, 1500);
        }
    }

    focusNextInput(inputId: string) {
        const currentIndex = this.extractIndexFromInputId(inputId);
        const currentQuestionCode = this.extractQuestionCode(inputId);
        const nextQuestionCode = currentQuestionCode + 1;
        const nextInput = this.findNextInput(currentIndex, currentQuestionCode, nextQuestionCode, inputId);

        if (nextInput) {
            this.blurCurrentInput(inputId);
            const inputElement = this.findInputElement(nextInput);
            if (inputElement) inputElement.focus();
        }
    }

    blurCurrentInput(inputId: string) {
        const currentInput = document.getElementById(inputId) as HTMLElement;
        currentInput.blur();
        if (currentInput.tagName === 'P-DROPDOWN') {
            const dropDownWrapper = document.getElementsByClassName('p-dropdown-items-wrapper');
            for (let i = 0; i < dropDownWrapper.length; i++) {
                const element = dropDownWrapper[i] as HTMLElement;
                element.style.display = 'none';
            }
        }
    }

    extractIndexFromInputId(inputId: string): number {
        return parseInt(inputId.substring(inputId.lastIndexOf('_') + 1), 10);
    }

    extractQuestionCode(inputId: string): number {
        const questionCodeWIndex = inputId.substring(0, inputId.length - 2);
        const currentQuestionCode = inputId.startsWith('val_2_')
            ? +questionCodeWIndex.substring(7)
            : +questionCodeWIndex.substring(1);
        return currentQuestionCode;
    }

    findNextInput(
        currentIndex: number,
        currentQuestionCode: number,
        nextQuestionCode: number,
        inputId: string,
    ): HTMLElement | null {
        const nextQcodeToString = this.generateQuestionCodeString(nextQuestionCode);
        const currentQcodeToString = this.generateQuestionCodeString(currentQuestionCode);

        if (inputId.startsWith('val_2_')) {
            const nextInputEntry = this.questionInstancesInputEntries.find((_, i) => i === currentIndex + 1);
            return nextInputEntry?.hidden
                ? document.getElementById(`${nextQcodeToString}_0`)
                : document.getElementById(`${currentQcodeToString}_${currentIndex + 1}`) ||
                      document.getElementById(`${nextQcodeToString}_0`);
        } else {
            return (
                document.getElementById(`val_2_${currentQcodeToString}_${currentIndex}`) ||
                document.getElementById(`${currentQcodeToString}_${currentIndex + 1}`) ||
                document.getElementById(`${nextQcodeToString}_0`)
            );
        }
    }

    generateQuestionCodeString(questionCode: number): string {
        return `q${questionCode.toString().padStart(3, '0')}`;
    }

    findInputElement(nextInput: HTMLElement): HTMLElement | null {
        return (
            nextInput.querySelector('p-dropdown') ||
            nextInput.querySelector('p-radiobutton') ||
            nextInput.querySelector('input')
        );
    }

    preloadTooltipContent() {
        const textKey = `question.${this.question.questionCode}.infoType.text`;
        const whereToFindKey = `question.${this.question.questionCode}.infoType.whereToFind`;
        const translatedText = this.translateService.keyExists(textKey)
            ? this.translateService.instant(textKey)
            : undefined;
        const whereToFind = this.translateService.keyExists(whereToFindKey)
            ? this.translateService.instant(whereToFindKey)
            : undefined;

        let tooltip = '';

        if (translatedText) {
            tooltip += `<p>${translatedText}</p>`;
        }

        if (translatedText && whereToFind) {
            tooltip += `<hr style="margin: 5px 0;">`;
        }

        if (whereToFind) {
            tooltip += `<p style="margin: 0;">${whereToFind}</p>`;
        }

        this.tooltipContent = tooltip;
    }
}
